import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Category, isProjectScope } from '../../../../../shared/issuesV2';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useParams, useQuery } from '../../../../contexts/RouterContext';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import OpenInFullPage from '../../../generic/Drawer/actions/OpenInFullPage';
import ShareLink from '../../../generic/Drawer/actions/ShareLink';
import SnippetsDrawerContent from '../../../projects/snippets/Drawer';
import { SNIPPET_DRAWER_SLIDE_DURATION } from '../../../projects/snippets/Drawer/constants';
import useSnippetUrl from '../../../projects/snippets/hooks/useSnippetUrls';
import Errors from '../Errors';
import LicensingIssueContent from '../IssueContent/LicensingIssueContent';
import QualityIssueContent from '../IssueContent/QualityIssueContent';
import VulnerabilityIssueContent from '../IssueContent/VulnerabilityIssueContent';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';
import useIssueDetailsTabs from '../Tabs/useIssueDetailsTabs';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { RevisionRouteParams } from '../../../projects/projectDashboardTypes';

type Props = {
  category: Category;
  /** The issue the drawer was opened for, so it can reload itself if Redux loses the details. */
  issueId: number;
};

/**
 * IssueDetailsDrawerContent displays the issue details content for use with the Dresser.
 * This component manages the content that appears inside the drawer, including handling
 * snippet mode and different issue types.
 */
const IssueDetailsDrawerContent: VoidFunctionComponent<Props> = ({ category, issueId }) => {
  const dispatch = useDispatch();
  const { errors, isLoading } = useSelector(IssuesV2Store.selectors.getAsyncStatusIssueDetails);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const [activeTab, setActiveTab] = useIssueDetailsTabs(true);
  const params = useParams<RevisionRouteParams>();
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const { pushDrawer, popDrawer, clearDrawers } = useDresser();
  const { isAnyModalOpen } = useIssueDetailsModals();

  // Check if we're in snippet mode based on query params
  const selectedSnippetId = queryParams.selectedSnippetId;
  const isSnippetMode = !!selectedSnippetId;

  // Get proper userRevisionLocator from route params
  const userRevisionLocator = params ? `${params.project_id}$${params.revision_id}` : '';
  const snippetUrl = useSnippetUrl(userRevisionLocator);

  const hasErrors = !!errors?.length;

  /*
    The drawer is mounted app-level via the Dresser, so it outlives the issues
    route. When that route unmounts it dispatches resetState(), which nulls
    issueDetails out from under an open drawer and leaves it a blank shell.
    Reload the issue instead of rendering that shell. Reloading once per issue
    is enough: a request that comes back empty without an error would otherwise
    retry forever.
  */
  const hasReloadedIssue = useRef(false);
  useEffect(() => {
    if (!issue && !isLoading && !hasErrors && !hasReloadedIssue.current) {
      hasReloadedIssue.current = true;
      dispatch(IssuesV2Store.actions.fetchIssueDetails(issueId));
    }
  }, [issue, isLoading, hasErrors, issueId, dispatch]);

  // Callback to switch to snippet mode - only available when in project scope
  const onShowSnippetDetailsCallback = useCallback((id: string) => {
    setQuery({ ...queryParams, selectedSnippetId: id });

    // Push the snippet drawer on top of the current issue drawer
    if (userRevisionLocator) {
      const onBackToIssueDrawer = () => {
        // Pop back to the issue details drawer (don't clear all drawers)
        setQuery({ ...queryParams, selectedSnippetId: undefined });
        popDrawer();
      };

      const onCloseSnippetDrawer = () => {
        // Clear all drawers when closing from snippet view (via X button)
        setQuery({ ...queryParams, selectedSnippetId: undefined });
        clearDrawers();
      };

      pushDrawer(
        <SnippetsDrawerContent userRevisionLocator={userRevisionLocator} />,
        {
          shouldSkipClickOutside: isAnyModalOpen,
          slideDuration: SNIPPET_DRAWER_SLIDE_DURATION,
          onBack: onBackToIssueDrawer,
          onClose: onCloseSnippetDrawer,
          buttonsSlot: (
            <>
              <OpenInFullPage url={snippetUrl} />
              <ShareLink url={snippetUrl} tooltipId="share-snippet-link" />
            </>
          ),
        },
      );
    }
  }, [queryParams, setQuery, userRevisionLocator, pushDrawer, popDrawer, clearDrawers, isAnyModalOpen, snippetUrl]);

  const onShowSnippetDetails = isProjectScope(scope) ? onShowSnippetDetailsCallback : undefined;

  // If we're already in snippet mode, this component shouldn't render
  // (the snippet drawer should have been pushed on top)
  if (isSnippetMode && selectedSnippetId && userRevisionLocator && isProjectScope(scope)) {
    return null;
  }

  if (hasErrors) {
    return <Errors errors={errors} />;
  }

  /*
    No details and nothing in flight yet: the reload above is about to start, so
    render nothing rather than the shell. Once it is loading the category
    content renders its own skeleton.
  */
  if (!issue && !isLoading) {
    return null;
  }

  if (category === Category.Vulnerability) {
    return (
      <VulnerabilityIssueContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    );
  }

  if (category === Category.Licensing) {
    return (
      <LicensingIssueContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onShowSnippetDetails={onShowSnippetDetails}
      />
    );
  }

  if (category === Category.Quality) {
    return (
      <QualityIssueContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    );
  }

  return null;
};

export default IssueDetailsDrawerContent;
